import html2canvas from 'html2canvas-pro'

export interface ScreenshotOptions {
  quality?: number // 0-1, default 0.85 pour WebP, 0.8 pour JPEG
  maxWidth?: number
  maxHeight?: number
  format?: 'png' | 'jpeg' | 'webp' // WebP est plus compact que JPEG
}

/**
 * Service pour capturer des screenshots de la page
 */
export class ScreenshotService {
  /**
   * Capturer un screenshot (par défaut via getDisplayMedia, fallback sur captureViewport)
   * Utilise getDisplayMedia si disponible, sinon html2canvas
   */
  static async capture(options: ScreenshotOptions = {}): Promise<{
    dataUrl: string
    blob: Blob
    mimeType: string
  }> {
    // Essayer getDisplayMedia en premier
    try {
      return await ScreenshotService.captureWithDisplayMedia(options)
    } catch (error) {
      console.warn(
        '[BdC-ScreenshotService] getDisplayMedia échoué, fallback sur captureViewport:',
        error
      )
      // Fallback sur captureViewport si getDisplayMedia échoue
      return await ScreenshotService.captureViewport(options)
    }
  }

  /**
   * Capturer un screenshot via l'API native getDisplayMedia
   * Capture vraiment ce que l'utilisateur voit à l'écran
   */
  static async captureWithDisplayMedia(options: ScreenshotOptions = {}): Promise<{
    dataUrl: string
    blob: Blob
    mimeType: string
  }> {
    console.log('[BdC-ScreenshotService] Capture screenshot via getDisplayMedia')

    try {
      // Vérifier que l'API est disponible
      if (!navigator.mediaDevices || !navigator.mediaDevices.getDisplayMedia) {
        throw new Error(
          "L'API getDisplayMedia n'est pas disponible dans ce navigateur. Veuillez utiliser un navigateur moderne."
        )
      }

      // Demander la permission de capturer l'écran
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          displaySurface: 'browser' as any, // Préférer l'onglet du navigateur (non typé dans TS)
        },
        audio: false,
      })

      // Récupérer la track vidéo
      const track = stream.getVideoTracks()[0]
      if (!track) {
        throw new Error('Aucune track vidéo disponible')
      }

      // Créer un élément video pour capturer le frame
      const video = document.createElement('video')
      video.srcObject = stream
      video.autoplay = true
      video.playsInline = true

      await new Promise<void>((resolve, reject) => {
        video.onloadedmetadata = () => {
          video.play().then(resolve).catch(reject)
        }
        video.onerror = reject
      })

      // Attendre un peu pour que la vidéo soit prête
      await new Promise(resolve => setTimeout(resolve, 200))

      // Créer un canvas et dessiner la vidéo
      const canvas = document.createElement('canvas')
      canvas.width = video.videoWidth
      canvas.height = video.videoHeight
      const ctx = canvas.getContext('2d')
      if (!ctx) {
        throw new Error('Impossible de créer le contexte canvas')
      }

      ctx.drawImage(video, 0, 0)

      // Arrêter le stream
      track.stop()
      stream.getTracks().forEach(t => t.stop())

      // Convertir en blob
      const format = options.format || 'png'
      const quality = options.quality ?? (format === 'jpeg' ? 0.92 : 1)
      const mimeType = format === 'jpeg' ? 'image/jpeg' : 'image/png'

      const blob = await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob(
          b => (b ? resolve(b) : reject(new Error('Erreur conversion blob'))),
          mimeType,
          quality
        )
      })

      const dataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader()
        reader.onloadend = () => {
          if (typeof reader.result === 'string') resolve(reader.result)
          else reject(new Error('Erreur conversion dataUrl'))
        }
        reader.onerror = reject
        reader.readAsDataURL(blob)
      })

      console.log('[BdC-ScreenshotService] Screenshot capturé via getDisplayMedia:', {
        size: blob.size,
        mimeType,
        dimensions: `${canvas.width}x${canvas.height}`,
      })

      return { dataUrl, blob, mimeType }
    } catch (error) {
      console.error('[BdC-ScreenshotService] Erreur capture getDisplayMedia:', error)
      throw new Error(
        `Erreur capture screenshot: ${error instanceof Error ? error.message : 'Erreur inconnue'}`
      )
    }
  }

  /**
   * Capturer un screenshot de la page visible (viewport)
   */
  static async captureViewport(options: ScreenshotOptions = {}): Promise<{
    dataUrl: string
    blob: Blob
    mimeType: string
  }> {
    console.log('[BdC-ScreenshotService] Capture screenshot viewport')

    const viewportWidth = options.maxWidth ?? window.innerWidth
    const viewportHeight = options.maxHeight ?? window.innerHeight
    const scrollX = window.scrollX || window.pageXOffset || 0
    const scrollY = window.scrollY || window.pageYOffset || 0
    const dpr = window.devicePixelRatio || 1

    try {
      // 1. Rendre la page entière (ou au moins le document) avec le scale par défaut (dpr)
      const fullCanvas = await html2canvas(document.documentElement, {
        useCORS: true,
        allowTaint: true,
        logging: false,
        // IMPORTANT : ne pas forcer scale: 1, laisser html2canvas utiliser devicePixelRatio
        backgroundColor: '#ffffff',
        // En général inutile de forcer width/height/x/y ici,
        // on laisse html2canvas gérer la mise en page
        windowWidth: document.documentElement.scrollWidth,
        windowHeight: document.documentElement.scrollHeight,
        scrollX,
        scrollY,
        foreignObjectRendering: false,
        removeContainer: true,
        imageTimeout: 15000,
        ignoreElements: element => {
          if (!element) return false
          return (
            element.id === 'bug-tracker-bubble' ||
            element.classList?.contains('bug-tracker-bubble') ||
            element.closest?.('.bug-tracker-bubble') !== null ||
            element.closest?.('[class*="bug-report"]') !== null
          )
        },
      })

      // 2. Créer un canvas strictement à la taille du viewport
      const outputCanvas = document.createElement('canvas')
      outputCanvas.width = viewportWidth * dpr
      outputCanvas.height = viewportHeight * dpr
      const ctx = outputCanvas.getContext('2d')
      if (!ctx) {
        throw new Error('Impossible de créer le contexte canvas')
      }

      // 3. Recouper à la position de scroll actuelle (en tenant compte du dpr)
      ctx.drawImage(
        fullCanvas,
        scrollX * dpr, // source x
        scrollY * dpr, // source y
        viewportWidth * dpr, // source width
        viewportHeight * dpr, // source height
        0, // dest x
        0, // dest y
        viewportWidth * dpr,
        viewportHeight * dpr
      )

      console.log('[BdC-ScreenshotService] Canvas viewport:', {
        fullCanvas: { width: fullCanvas.width, height: fullCanvas.height },
        outputCanvas: { width: outputCanvas.width, height: outputCanvas.height },
        viewportWidth,
        viewportHeight,
        scrollX,
        scrollY,
        dpr,
      })

      // Utiliser le canvas de sortie pour la conversion
      const canvas = outputCanvas

      // Utiliser PNG par défaut pour préserver la qualité du texte
      // PNG sans perte garantit que le texte reste net même après compression
      const format = options.format || 'png'
      let quality = options.quality
      let mimeType: string

      if (format === 'webp') {
        quality = quality ?? 0.95 // Qualité élevée pour WebP (préserver le texte)
        mimeType = 'image/webp'
      } else if (format === 'jpeg') {
        quality = quality ?? 0.92 // Qualité élevée pour JPEG (préserver le texte)
        mimeType = 'image/jpeg'
      } else {
        quality = 1 // PNG sans perte (meilleur pour le texte)
        mimeType = 'image/png'
      }

      console.log('[BdC-ScreenshotService] Conversion en', mimeType, 'avec qualité', quality)

      // Tester si WebP est supporté
      let blob: Blob
      if (format === 'webp') {
        try {
          blob = await new Promise<Blob>((resolve, reject) => {
            canvas.toBlob(
              blob => {
                if (blob) {
                  resolve(blob)
                } else {
                  reject(new Error('Erreur lors de la conversion en blob WebP'))
                }
              },
              'image/webp',
              quality
            )
          })
        } catch {
          console.warn('[BdC-ScreenshotService] WebP non supporté, fallback sur JPEG')
          // Fallback sur JPEG si WebP n'est pas supporté
          mimeType = 'image/jpeg'
          quality = 0.8
          blob = await new Promise<Blob>((resolve, reject) => {
            canvas.toBlob(
              blob => {
                if (blob) {
                  resolve(blob)
                } else {
                  reject(new Error('Erreur lors de la conversion en blob JPEG'))
                }
              },
              'image/jpeg',
              quality
            )
          })
        }
      } else {
        blob = await new Promise<Blob>((resolve, reject) => {
          canvas.toBlob(
            blob => {
              if (blob) {
                resolve(blob)
              } else {
                reject(new Error('Erreur lors de la conversion en blob'))
              }
            },
            mimeType,
            quality
          )
        })
      }

      console.log('[BdC-ScreenshotService] Screenshot capturé:', {
        size: blob.size,
        mimeType,
        dimensions: `${canvas.width}x${canvas.height}`,
      })

      // Créer le dataUrl à partir du blob
      const dataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader()
        reader.onloadend = () => {
          if (typeof reader.result === 'string') {
            resolve(reader.result)
          } else {
            reject(new Error('Erreur lors de la conversion en dataUrl'))
          }
        }
        reader.onerror = reject
        reader.readAsDataURL(blob)
      })

      return { dataUrl, blob, mimeType }
    } catch (error) {
      console.error('[BdC-ScreenshotService] Erreur lors de la capture:', error)
      throw new Error(
        `Erreur capture screenshot: ${error instanceof Error ? error.message : 'Erreur inconnue'}`
      )
    }
  }

  /**
   * Capturer un screenshot de la page complète (avec scroll)
   */
  static async captureFullPage(options: ScreenshotOptions = {}): Promise<{
    dataUrl: string
    blob: Blob
    mimeType: string
  }> {
    console.log('[BdC-ScreenshotService] Capture screenshot page complète')

    const canvas = await html2canvas(document.body, {
      useCORS: true,
      logging: false,
      scale: 1,
      width: document.body.scrollWidth,
      height: document.body.scrollHeight,
      windowWidth: document.body.scrollWidth,
      windowHeight: document.body.scrollHeight,
    })

    const format = options.format || 'png'
    const quality = options.quality || (format === 'jpeg' ? 0.92 : 1)
    const mimeType = format === 'jpeg' ? 'image/jpeg' : 'image/png'

    const dataUrl = canvas.toDataURL(mimeType, quality)
    const blob = await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        blob => {
          if (blob) {
            resolve(blob)
          } else {
            reject(new Error('Erreur lors de la conversion en blob'))
          }
        },
        mimeType,
        quality
      )
    })

    console.log('[BdC-ScreenshotService] Screenshot page complète capturé:', {
      size: blob.size,
      mimeType,
      dimensions: `${canvas.width}x${canvas.height}`,
    })

    return { dataUrl, blob, mimeType }
  }

  /**
   * Compresser un blob image pour réduire sa taille (max 5MB pour préserver la qualité)
   */
  static async compressImage(
    blob: Blob,
    maxSizeBytes: number = 5 * 1024 * 1024, // 5MB (augmenté pour préserver la qualité)
    quality: number = 0.95
  ): Promise<Blob> {
    if (blob.size <= maxSizeBytes) {
      return blob
    }

    console.log('[BdC-ScreenshotService] Compression image:', {
      originalSize: blob.size,
      targetSize: maxSizeBytes,
    })

    return new Promise((resolve, reject) => {
      const img = new Image()
      const url = URL.createObjectURL(blob)

      img.onload = () => {
        URL.revokeObjectURL(url)

        const canvas = document.createElement('canvas')
        let width = img.width
        let height = img.height

        // Réduire la taille si nécessaire, mais préserver le ratio
        // On réduit progressivement jusqu'à atteindre la taille cible
        const targetPixels = (maxSizeBytes / 4) * 1000 // Estimation pixels pour la taille cible
        const currentPixels = width * height

        if (currentPixels > targetPixels) {
          const ratio = Math.sqrt(targetPixels / currentPixels)
          width = Math.floor(width * ratio)
          height = Math.floor(height * ratio)
        }

        canvas.width = width
        canvas.height = height

        const ctx = canvas.getContext('2d')
        if (!ctx) {
          reject(new Error('Impossible de créer le contexte canvas'))
          return
        }

        // Utiliser un meilleur algorithme de redimensionnement pour préserver la netteté
        ctx.imageSmoothingEnabled = true
        ctx.imageSmoothingQuality = 'high'
        ctx.drawImage(img, 0, 0, width, height)

        // Déterminer le format de sortie selon le format d'entrée
        const inputMimeType = blob.type
        let outputMimeType = 'image/png'
        let outputQuality = 1

        if (inputMimeType === 'image/png') {
          // Pour PNG, on essaie WebP avec haute qualité pour réduire la taille
          // Si WebP n'est pas supporté, on garde PNG
          outputMimeType = 'image/webp'
          outputQuality = quality
        } else if (inputMimeType === 'image/webp') {
          outputMimeType = 'image/webp'
          outputQuality = quality
        } else {
          outputMimeType = 'image/jpeg'
          outputQuality = quality
        }

        canvas.toBlob(
          compressedBlob => {
            if (compressedBlob) {
              // Si WebP a échoué et qu'on avait un PNG, fallback sur PNG
              if (
                outputMimeType === 'image/webp' &&
                inputMimeType === 'image/png' &&
                compressedBlob.size > blob.size * 0.9
              ) {
                canvas.toBlob(
                  pngBlob => {
                    if (pngBlob) {
                      console.log('[BdC-ScreenshotService] Image compressée (PNG):', {
                        originalSize: blob.size,
                        compressedSize: pngBlob.size,
                        reduction: `${Math.round((1 - pngBlob.size / blob.size) * 100)}%`,
                      })
                      resolve(pngBlob)
                    } else {
                      resolve(compressedBlob)
                    }
                  },
                  'image/png',
                  1
                )
              } else {
                console.log('[BdC-ScreenshotService] Image compressée:', {
                  originalSize: blob.size,
                  compressedSize: compressedBlob.size,
                  reduction: `${Math.round((1 - compressedBlob.size / blob.size) * 100)}%`,
                })
                resolve(compressedBlob)
              }
            } else {
              reject(new Error('Erreur lors de la compression'))
            }
          },
          outputMimeType,
          outputQuality
        )
      }

      img.onerror = () => {
        URL.revokeObjectURL(url)
        reject(new Error("Erreur lors du chargement de l'image"))
      }

      img.src = url
    })
  }

  /**
   * Convertir un blob en base64
   */
  static async blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onloadend = () => {
        if (typeof reader.result === 'string') {
          resolve(reader.result)
        } else {
          reject(new Error('Erreur lors de la conversion en base64'))
        }
      }
      reader.onerror = reject
      reader.readAsDataURL(blob)
    })
  }
}
